
import { generateFieldString } from '@/codeGeneration/msgFileGen';
import { V4UUID } from '@/firebase/database';
import {
  toCamelCase,
  toPascalCase,
  toSnakeCase
} from '@/modules/globalModule/components/helperMethods';
import { useGlobalStore } from '@/modules/globalModule/store';
import { Node } from '@/modules/nodesCanvasModule/components/storeClasses/node';
import { Message } from './storeClasses/message';
import { Service } from './storeClasses/service';
import { Topic } from './storeClasses/topic';
import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';

import { defineComponent, PropType } from 'vue';

interface InterfaceItem {
  item: Topic | Node | Service;
  type: string;
}
export default defineComponent({
  name: 'ActiveMessagesPopup',
  data(): {
    interFilter: string;
    messageHistory: Array<V4UUID>;
    historyIndex: number;
    openMessageText?: Array<{ fieldText: string; id?: string }>;
    openMessageName: string;
  } {
    return {
      interFilter: '',
      messageHistory: [],
      historyIndex: -1,
      openMessageText: undefined,
      openMessageName: ''
    };
  },
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: false
    },
    setVisible: {
      type: Function as PropType<(visible: boolean) => void>,
      required: true
    },
    packages: {
      type: Array as PropType<Array<PackageT>>,
      required: false,
      default: []
    },
    initFilter: {
      type: String,
      required: false,
      default: ''
    }
  },

  methods: {
    getInterfaces(): {
      topics: Topic[];
      services: Service[];
      nodes: Node[];
    } {
      return {
        topics: this.packages.reduce(
          (accumulator: Topic[], pkg: PackageT) =>
            accumulator.concat(pkg.topics),
          []
        ),
        services: this.packages.reduce(
          (accumulator: Service[], pkg: PackageT) =>
            accumulator.concat(pkg.services),
          []
        ),
        nodes: this.packages.reduce(
          (accumulator: Node[], pkg: PackageT) => accumulator.concat(pkg.nodes),
          []
        )
      };
    },
    getInterfacesList(): Array<InterfaceItem> {
      let interfacesList: Array<InterfaceItem> = [];
      return interfacesList
        .concat(
          this.getInterfaces().topics.map((topic: Topic) => {
            return { item: topic, type: 'topic' };
          })
        )
        .concat(
          this.getInterfaces().services.map((service: Service) => {
            return { item: service, type: 'service' };
          })
        )
        .concat(
          this.getInterfaces().nodes.map((node: Node) => {
            return { item: node, type: 'node' };
          })
        );
    },
    getFilteredInterfaces(): Array<InterfaceItem> {
      return this.getInterfacesList().filter(
        (item: InterfaceItem) =>
          item.type === this.interFilter || this.interFilter === ''
      );
    },
    filterInterfaces(newFilter: string) {
      if (this.interFilter === newFilter) {
        this.interFilter = '';
      } else {
        this.interFilter = newFilter;
      }
    },
    exit() {
      this.setVisible(false);
      this.openInterfacesView();
    },
    openInterfacesView() {
      this.interFilter = '';
      this.openMessageText = undefined;
      this.openMessageName = '';
      this.messageHistory = [];
    },
    // opens an interface from the topic viewing menu
    openInterface(inter: Node | Topic | Service, type: string) {
      this.messageHistory = [];
      this.historyIndex = 0;
      if (type === 'topic') {
        this.openMessage((inter as Topic).msgType);
        this.messageHistory.push((inter as Topic).msgType);
      } else if (type === 'service') {
        this.openService(inter.id);
        this.messageHistory.push(inter.id);
      }
    },
    incrementHistory(increment: number = 1) {
      if (
        this.messageHistory.length < 1 ||
        this.historyIndex + increment >= this.messageHistory.length
      )
        return;
      this.historyIndex = this.historyIndex + increment;
      if (this.historyIndex < 0) {
        this.openInterfacesView();
        return;
      }
      let globalStore = useGlobalStore();
      let type = globalStore.getTypeOfElemById(
        this.messageHistory[this.historyIndex]
      );
      if (type === 'Message') {
        this.openMessage(this.messageHistory[this.historyIndex]);
      } else if (type === 'Service') {
        this.openService(this.messageHistory[this.historyIndex]);
      }
    },
    // opens message from link, increments history too
    openMessageLink(id: V4UUID) {
      if (this.openMessage(id)) {
        this.messageHistory = this.messageHistory.slice(
          0,
          this.historyIndex + 1
        );
        this.messageHistory.push(id);
        this.historyIndex += 1;
      }
    },
    openMessage(id: V4UUID): boolean {
      let globalStore = useGlobalStore();
      let targetMessage: Message | undefined = globalStore.getMessage(id);
      console.log(targetMessage);
      if (!targetMessage || !globalStore.workspaceId) return false;
      let msgPkg = globalStore.getElemPackage(targetMessage.id);
      if (!msgPkg) return false;
      this.openMessageText = [];
      this.openMessageName = `${toCamelCase(targetMessage.name)}.msg`;
      for (let field of targetMessage.fields) {
        this.openMessageText.push({
          fieldText: generateFieldString(
            field,
            globalStore.workspaceId,
            msgPkg.id
          ),
          id: field.msg
        });
      }
      return true;
    },
    openService(id: V4UUID) {
      let globalStore = useGlobalStore();
      let targetService: Service | undefined = globalStore.getService(id);
      if (!targetService || !globalStore.workspaceId) return;
      let srvPkg = globalStore.getElemPackage(targetService.id);
      if (!srvPkg) return;
      this.openMessageText = [];
      this.openMessageName = `${toCamelCase(targetService.name)}.srv`;
      for (let reqField of targetService.requestList) {
        this.openMessageText.push({
          fieldText: generateFieldString(
            reqField,
            globalStore.workspaceId,
            srvPkg.id
          ),
          id: reqField.msg
        });
      }
      this.openMessageText.push({ fieldText: '---' });
      for (let resField of targetService.responseList) {
        this.openMessageText.push({
          fieldText: generateFieldString(
            resField,
            globalStore.workspaceId,
            srvPkg.id
          ),
          id: resField.msg
        });
      }
    },
    getInterfaceTypeString(
      inter: Node | Topic | Service,
      type: string
    ): string {
      const globalStore = useGlobalStore();
      if (type === 'topic') {
        let targetMessage: Message | undefined = globalStore.getMessage(
          (inter as Topic).msgType
        );
        if (targetMessage && targetMessage.fullMessageString)
          return targetMessage.fullMessageString;
        return '';
      } else if (type === 'service') {
        let elemPackage = globalStore.getElemPackage(inter.id);
        if (!elemPackage) {
          elemPackage = this.packages.find((pkg) =>
            pkg.services.find((srv) => srv.id === inter.id)
          );
        }
        if (elemPackage)
          return `${toSnakeCase(elemPackage.name)}/srv/${toPascalCase(
            inter.name
          )}`;
        return `${toPascalCase(inter.name)}.srv`;
      }
      return '';
    }
  },
  watch: {
    visible() {
      if (this.initFilter !== this.interFilter) {
        this.interFilter = this.initFilter;
      }
    }
  }
});
